// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Remove from "@adl/XDL/Icons/Remove";
import { color, mergeWithCss, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    extendedStyles?: ProductLineRemoveButtonStyles;
    onRemove: () => void;
}

export interface ProductLineRemoveButtonStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}
const productLineRemoveButtonStyles: ProductLineRemoveButtonStyles = {
    wrapper: {
        css: css`
            min-width: 40px;
            position: relative;
        `,
    },
    innerWrapper: {
        css: css`
            visibility: hidden;
            position: absolute;
            right: 0;
            bottom: 0;
            ${xScreen("lte", "md")} {
                visibility: visible;
                position: static;
                ${IconWrapper} {
                    margin-bottom: 5px;
                    color: ${color("danger")};
                }
            }
        `,
    },
    link: {
        color: "danger.light",
        typographyProps: {
            size: 10,
            weight: 600,
            css: css`
                letter-spacing: 0;
                line-height: 11px;
                display: flex;
                flex-direction: column;
                align-items: center;
                > ${IconWrapper} {
                    margin-bottom: 5px;
                    color: ${color("danger", 0.75)};
                    ${xScreen("lte", "md")} {
                        margin-bottom: 0;
                    }
                }
            `,
        },
        css: css`
            margin-top: 15px;
            ${xScreen("lte", "md")} {
                & > span {
                    flex-direction: row;
                    span:last-child {
                        margin-left: 4px;
                        color: ${color("danger")};
                    }
                }
            }
            &:hover {
                span {
                    color: ${color("danger")};
                    ${IconWrapper} {
                        color: ${color("danger")};
                    }
                }
            }
        `,
    },
};

const ProductLineRemoveButton = ({ extendedStyles, onRemove }: Props) => {
    const [styles] = React.useState(() => mergeWithCss(productLineRemoveButtonStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="product_removeLine">
            <StyledWrapper {...styles.innerWrapper}>
                <Link onClick={onRemove} {...styles.link}>
                    <Icon src={Remove} size={12} />
                    <Typography>{translate("Remove")}</Typography>
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProductLineRemoveButton;
